import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress
} from "@mui/material";
import {
  Save as SaveIcon,
  Download as DownloadIcon,
  Undo as Undo2Icon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import DatabaseUIConfigGrid from './DatabaseUIConfigGrid';
import { useApiClient } from '../../config/axios';
const DOC_ID = 'Assortment'; // or whichever doc_id you chose

// ------------------------------
// Utility helpers
// ------------------------------

function download(filename: string, text: string) {
  const element = document.createElement("a");
  const file = new Blob([text], { type: "application/json" });
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

// ------------------------------
// Main Editor Shell
// ------------------------------

type EditorProps = {
  /** Provide initial JSON. If omitted, the editor will start empty and allow import. */
  initialJson?: any;
  /** Optional name to use when downloading. */
  filename?: string;
  /** Optional callback when content changes. */
  onChange?: (updated: any) => void;
};

const UIConfigurationEditor: React.FC<EditorProps> = ({
  initialJson,
  filename = "AssortmentUIConf.confdefn",
  onChange
}) => {
  const apiClient = useApiClient();
  const [original, setOriginal] = useState<any>(initialJson ?? {});
  const [data, setData] = useState<any>(initialJson ?? {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const dirty = JSON.stringify(data) !== JSON.stringify(original);

  useEffect(() => { onChange?.(data); }, [data, onChange]);

  const loadFromDatabase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: json } = await apiClient.get(`/conf/${encodeURIComponent(DOC_ID)}/json`);
      setOriginal(json);
      setData(json);
      setHasLoadedOnce(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load configuration');
      console.error('Config fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data only once on mount
  useEffect(() => {
    if (!initialJson && !hasLoadedOnce) {
      loadFromDatabase();
    }
  }, []); // Empty dependency array - only run once on mount

  const save = async () => {
    try {
      setLoading(true);
      setError(null);

      // New endpoint accepts the raw JSON body
      await apiClient.put(`/conf/${encodeURIComponent(DOC_ID)}/json`, data, {
        headers: { 'Content-Type': 'application/json' }
      });

      setOriginal(deepClone(data));
      setSuccess('Configuration saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save configuration');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportFile = () => {
    download(filename, JSON.stringify(data, null, 2));
  };

  const importFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        setOriginal(parsed);
        setData(parsed);
        setSuccess("File imported successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } catch (e) {
        setError("Invalid JSON file.");
      }
    };
    reader.readAsText(file);
    setImportDialogOpen(false);
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Assortment Configuration...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', width: '100%', p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            UI Configuration Editor
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Box>

      {/* Main Content Area - Database-backed Excel View */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>

        {/* Database-backed Excel DataGrid */}
        <DatabaseUIConfigGrid
          configId={DOC_ID}
          onChange={(changes) => {
            console.log('Database changes:', changes);
          }}
        />
      </Box>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Import JSON file</DialogTitle>
        <DialogContent>
          <input
            type="file"
            accept="application/json,.json,.confdefn"
            onChange={(e) => e.target.files && e.target.files[0] && importFile(e.target.files[0])}
            style={{ margin: '16px 0' }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UIConfigurationEditor;